import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box, Button, TextField, Typography, Table, TableHead, TableRow,
  TableCell, TableBody, Paper, Dialog, DialogActions, DialogContent,
  DialogTitle, Snackbar, Alert
} from "@mui/material";

const LearningGoalsAdmin = () => {
  const [goals, setGoals] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ id: null, name: "" });
  const [errorMsg, setErrorMsg] = useState("");
  const [showError, setShowError] = useState(false);

  const apiBase = `${process.env.REACT_APP_API_URL}/profile/admin/learning-goals`;

  const fetchGoals = async () => {
    try {
      const token = localStorage.getItem("jwtToken");
      const res = await axios.get(apiBase, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGoals(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchGoals();
  }, []);

  const openDialog = (goal = { id: null, name: "" }) => {
    setForm(goal);
    setDialogOpen(true);
    setErrorMsg("");
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("jwtToken");
      const headers = { Authorization: `Bearer ${token}` };

      if (form.id) {
        await axios.put(`${apiBase}/${form.id}`, form, { headers });
      } else {
        await axios.post(apiBase, form, { headers });
      }

      setDialogOpen(false);
      fetchGoals();
    } catch (err) {
      console.error(err);
      setErrorMsg("Errore durante il salvataggio.");
      setShowError(true);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo obiettivo?")) return;

    try {
      const token = localStorage.getItem("jwtToken");
      const headers = { Authorization: `Bearer ${token}` };

      await axios.delete(`${apiBase}/${id}`, { headers });
      fetchGoals();
      setErrorMsg("");
    } catch (err) {
      const errData = err.response?.data;

      if (errData?.associated_users || errData?.associated_exercises) {
        const users = errData.associated_users.map(u => `👤 ${u.name}`).join("\n");
        const exercises = errData.associated_exercises.map(e => `🎵 ${e.note}`).join("\n");

        const details = [
          users && `**Utenti associati:**\n${users}`,
          exercises && `**Esercizi associati:**\n${exercises}`
        ].filter(Boolean).join("\n\n");

        setErrorMsg(`❌ Impossibile eliminare il learning goal:\n\n${details}`);
        setShowError(true);
      } else {
        setErrorMsg("Errore durante l'eliminazione.");
        setShowError(true);
      }
    }
  };

  return (
    <Box p={2}>
      <Typography variant="h5">Gestione Learning Goals</Typography>
      <Button onClick={() => openDialog()} variant="contained" sx={{ mt: 2 }}>
        Nuovo Obiettivo
      </Button>

      <Paper sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {goals.map(goal => (
              <TableRow key={goal.id}>
                <TableCell>{goal.name}</TableCell>
                <TableCell>
                  <Button onClick={() => openDialog(goal)}>Modifica</Button>
                  <Button color="error" onClick={() => handleDelete(goal.id)}>Elimina</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{form.id ? "Modifica" : "Nuovo"} Learning Goal</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Nome"
            fullWidth
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleSave}>Salva</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar per errori */}
      <Snackbar
        open={showError}
        autoHideDuration={8000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity="error"
          sx={{ whiteSpace: 'pre-wrap', width: '100%' }}
        >
          {errorMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LearningGoalsAdmin;
